import { useCallback, useEffect, useState, useRef } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import type { Dimensions } from '@shipstation/client-common/types/dimensions';
import type { Weight } from '@shipstation/client-common/types/weight';

import { USD } from '../../../../../../constants/currency-codes';
import { emptyArray, emptyObject } from 'ui-components/default-props/empty';
import { useEnableMultiPackageItemAllocation } from '../../../../../../feature-flags/hooks/use-enable-multi-package-item-allocation';
import { STANDARD_PACKAGE_ID } from '../../../../../../services/package-service';
import Global from '../../../../../../store/global-selectors';
import { updateFulfillmentPlanEntitiesOptimistically } from '../../../../../../store/entities/entities.actions';
import { useFulfillmentPlannerContext } from '../../../../../../fulfillment-planner/context';
import { populatePackagesWithAssignments } from '../helpers/extract-item-assignments';
import type { PackageWithTareWeight } from '../utils/calculate-package-weight';
import { calculatePackageWeight } from '../utils/calculate-package-weight';
import { UnitConversionService } from '../../../../../../services/unit-conversion-service';
import { convertDimensions } from '../../../../../../services/unit-services/dimensions/dimension-convert-service';
import { WeightUnit } from '../../../../../../enums/weight-unit';
import { DimensionUnit } from '../../../../../../enums/dimension-unit';
import type { AssignedItem, Package, OrderItem } from '../../../../../../components/modals/multi-package-modal-dnd/types';

import { logPackingRecommendationActivity, PackingRecommendationAction } from '../../../../../../packing/services/packing-activity-service';
import {
  trackPackingRecommendationApplied,
  trackPackingRecommendationCleared,
  trackPackingRecommendationDismissed,
} from '../../../../../../packing/services/packing-recommendations-analytics';

import { messages } from '../messages';
import type { BoxResult } from '../types';
import { buildBoxesFromPlanPackages } from '../utils/build-boxes-from-plan-packages';
import type { SellerPackage } from '../utils/build-boxes-from-plan-packages';
import {
  consumeSystemPackageChange,
  consumeUserPackageEdit,
  getAppliedState,
  getCachedResult,
  hasError,
  isCurrentlyCalculating,
  isMultiPackageDrawerOpen,
  isPersistedAppliedDismissed,
  markPackingApplied,
  markPersistedAppliedDismissed,
  onAppliedChange,
  resetPackingApplied,
  type AppliedPackageSnapshot,
} from '../use-packing-applied';
import {
  getTerminalPackingRecommendationStatus,
  PackingRecommendationStatus,
} from '../../../../../../packing/services/packing-recommendation-status';
import type { JobResultResponse } from '../../../../../../packing/services/packing-service';
import type { PackingRecommendationGuard } from '../../../../../../packing/enforcement/use-packing-recommendation-guard';

const DIMENSION_MATCH_TOLERANCE = 0.5;
const WEIGHT_CHANGE_TOLERANCE_OUNCES = 0.1;
const DIMENSION_CHANGE_TOLERANCE_INCHES = 0.1;

type PlanPackage = {
  packageId?: string;
  packageTypeId?: string;
  weight?: { value?: number; unit?: string };
  dimensions?: AppliedPackageSnapshot['dimensions'];
  assignedItems?: AssignedItem[];
  insuredValue?: Package['insuredValue'];
  contentDescription?: string;
};

const toAppliedSnapshot = (p: PlanPackage): AppliedPackageSnapshot => ({
  packageId: p.packageId,
  packageTypeId: p.packageTypeId,
  weight: p.weight?.value,
  weightUnit: p.weight?.unit,
  dimensions: p.dimensions,
});

const hasRecommendedFieldChanged = (current: PlanPackage, applied: AppliedPackageSnapshot): boolean => {
  const isSamePackage =
    current.packageTypeId != null && applied.packageTypeId != null
      ? String(current.packageTypeId) === String(applied.packageTypeId)
      : current.packageId === applied.packageId;
  if (!isSamePackage) return true;

  if (current.weight?.value != null && applied.weight != null) {
    const currentOunces = UnitConversionService.convertValue(
      current.weight.value,
      current.weight.unit ?? WeightUnit.Ounces,
      WeightUnit.Ounces,
    );
    const appliedOunces = UnitConversionService.convertValue(applied.weight, applied.weightUnit ?? WeightUnit.Ounces, WeightUnit.Ounces);
    if (Math.abs(currentOunces - appliedOunces) > WEIGHT_CHANGE_TOLERANCE_OUNCES) return true;
  }

  if (current.dimensions && applied.dimensions) {
    const currentInches = convertDimensions(current.dimensions, DimensionUnit.Inches);
    const appliedInches = convertDimensions(applied.dimensions, DimensionUnit.Inches);
    return (['length', 'width', 'height'] as const).some(
      (side) => Math.abs(currentInches[side] - appliedInches[side]) > DIMENSION_CHANGE_TOLERANCE_INCHES,
    );
  }
  return false;
};

const sortFn = (a: AssignedItem, b: AssignedItem) => a.orderItemId.localeCompare(b.orderItemId);
const assignmentsChanged = (applied: AssignedItem[], current: AssignedItem[]): boolean => {
  if (applied.length !== current.length) return true;
  const sortedApplied = [...applied].sort(sortFn);
  const sortedCurrent = [...current].sort(sortFn);
  return sortedApplied.some((a, i) => a.orderItemId !== sortedCurrent[i].orderItemId || a.quantity !== sortedCurrent[i].quantity);
};

type SignatureItem = {
  fulfillmentPlanItemId?: string;
  orderItemId?: string;
  salesOrderItemId?: string;
  quantity?: number;
};

const buildItemsSignature = (items: readonly SignatureItem[]): string =>
  JSON.stringify(
    items
      .map((i) => ({ id: i.fulfillmentPlanItemId ?? i.orderItemId ?? i.salesOrderItemId, q: i.quantity ?? 0 }))
      .sort((a, b) => String(a.id).localeCompare(String(b.id))),
  );

type FulfillmentPlannerContextCallbacks = {
  onMultiPackageSave?: (packages: unknown[]) => void;
};

type UseApplyRecommendationReturn = {
  handleApply: () => void;
  isApplied: boolean;
  appliedPackageName: string | null;
  clearApplied: () => void;
};

export const useApplyRecommendation = (
  result: JobResultResponse | null,
  availablePackages: Array<Record<string, unknown>>,
  fulfillmentPlanId: string,
  sellerDimensionUnit: string,
  sellerWeightUnit: string,
  orderItems: OrderItem[] = [],
  packingRecommendationGuard: PackingRecommendationGuard,
  currentPackagesOverride?: Package[],
): UseApplyRecommendationReturn => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const context = useFulfillmentPlannerContext();
  const fulfillmentPlan = useSelector((state) => Global.selectEntitiesFulfillmentPlanById(state, { fulfillmentPlanId }));
  const currentPackages = (currentPackagesOverride ?? fulfillmentPlan?.packages ?? emptyArray) as PlanPackage[];

  const persistedState = getAppliedState(fulfillmentPlanId);
  const backendStatus = getTerminalPackingRecommendationStatus(fulfillmentPlan);
  const isBackendApplied = backendStatus === PackingRecommendationStatus.Applied && !isPersistedAppliedDismissed(fulfillmentPlanId);
  const [isApplied, setIsApplied] = useState(persistedState?.isApplied ?? isBackendApplied);
  const [appliedPackageName, setAppliedPackageName] = useState<string | null>(persistedState?.packageName ?? null);
  const [appliedValues, setAppliedValues] = useState<AppliedPackageSnapshot[] | null>(persistedState?.appliedPackages ?? null);
  const isApplyingRef = useRef(false);
  const appliedPackageNameRef = useRef<string | null>(persistedState?.packageName ?? null);
  const hasDismissedRef = useRef(false);
  const hasGuardedChangeRef = useRef(false);
  const hasCheckedInitialDivergenceRef = useRef(false);
  const lastSeenPackagesRef = useRef(currentPackages);

  const clearAppliedState = useCallback(() => {
    setIsApplied(false);
    setAppliedPackageName(null);
    setAppliedValues(null);
    isApplyingRef.current = false;
    appliedPackageNameRef.current = null;
    hasDismissedRef.current = false;
    hasGuardedChangeRef.current = false;
  }, []);

  useEffect(() => {
    return onAppliedChange((fpId, packageName) => {
      if (fpId !== fulfillmentPlanId) return;
      if (packageName === null) {
        clearAppliedState();
        return;
      }
      setIsApplied(true);
      setAppliedPackageName(packageName);
      appliedPackageNameRef.current = packageName;
      hasDismissedRef.current = false;
      setAppliedValues(getAppliedState(fulfillmentPlanId)?.appliedPackages ?? null);
    });
  }, [fulfillmentPlanId, clearAppliedState]);

  const { onMultiPackageSave } = (context ?? emptyObject) as FulfillmentPlannerContextCallbacks;
  const isMultiPackageItemAllocationEnabled = useEnableMultiPackageItemAllocation();

  useEffect(() => {
    const existing = getAppliedState(fulfillmentPlanId);
    if (backendStatus !== PackingRecommendationStatus.Applied) {
      if (existing?.hydratedFromBackend) {
        resetPackingApplied(fulfillmentPlanId);
      }
      return;
    }
    if (existing || isPersistedAppliedDismissed(fulfillmentPlanId)) return;
    if (isCurrentlyCalculating(fulfillmentPlanId) || getCachedResult(fulfillmentPlanId) !== null || hasError(fulfillmentPlanId)) return;
    if (currentPackages.length === 0) return;

    const planItems = (fulfillmentPlan?.items ?? emptyArray) as OrderItem[];
    const boxes = buildBoxesFromPlanPackages(currentPackages, availablePackages as SellerPackage[], planItems);
    const isMultiBox = currentPackages.length > 1;
    const packageName = isMultiBox
      ? intl.formatMessage(messages.packagesConfigured, { count: currentPackages.length })
      : intl.formatMessage(messages.packageConfigured, { name: boxes[0]?.box?.name ?? '' });

    markPackingApplied({
      fulfillmentPlanId,
      packageName,
      isMultiBox,
      boxes,
      appliedPackages: currentPackages.map(toAppliedSnapshot),
      appliedAssignments: currentPackages.map((p) => p.assignedItems ?? []),
      hydratedFromBackend: true,
      appliedItemsSignature: buildItemsSignature(planItems),
    });
  }, [fulfillmentPlanId, backendStatus, currentPackages, availablePackages, fulfillmentPlan, intl]);

  const findPackageByBox = useCallback(
    (box: BoxResult['box']) => {
      if (!box) return null;

      if (box.packageId) {
        const byId = availablePackages.find((pkg: { packageId?: string }) => pkg.packageId === box.packageId);
        if (byId) return byId;
      }

      if (box.name) {
        const byName = availablePackages.find((pkg: { name?: string }) => pkg.name?.toLowerCase() === box.name?.toLowerCase());
        if (byName) return byName;
      }

      if (box.length && box.width && box.height) {
        const byDimensions = availablePackages.find(
          (pkg: { dimensions?: Record<string, unknown>; length?: number; width?: number; height?: number }) => {
            const pkgDims = pkg.dimensions ?? {};
            const pkgLength = Number(pkgDims.length) || Number(pkg.length) || 0;
            const pkgWidth = Number(pkgDims.width) || Number(pkg.width) || 0;
            const pkgHeight = Number(pkgDims.height) || Number(pkg.height) || 0;

            return (
              Math.abs(pkgLength - box.length) <= DIMENSION_MATCH_TOLERANCE &&
              Math.abs(pkgWidth - box.width) <= DIMENSION_MATCH_TOLERANCE &&
              Math.abs(pkgHeight - box.height) <= DIMENSION_MATCH_TOLERANCE
            );
          },
        );
        if (byDimensions) return byDimensions;
      }

      return null;
    },
    [availablePackages],
  );

  const formatBoxDimensions = useCallback(
    (box: BoxResult['box']) => {
      if (!box) return null;
      return {
        length: Math.round(box.length * 10) / 10,
        width: Math.round(box.width * 10) / 10,
        height: Math.round(box.height * 10) / 10,
        unit: sellerDimensionUnit,
      };
    },
    [sellerDimensionUnit],
  );

  const buildCompositePackageId = useCallback(
    (selectedPackage: { packageTypeId?: string; isCustom?: boolean; carrierId?: number } | null) => {
      if (!selectedPackage) {
        return `${STANDARD_PACKAGE_ID}-false-0`;
      }
      return `${selectedPackage.packageTypeId}-${selectedPackage.isCustom ?? false}-${selectedPackage.carrierId ?? 0}`;
    },
    [],
  );

  const convertBoxToPackage = useCallback(
    (boxResult: BoxResult, existingPackage?: { insuredValue?: Package['insuredValue']; contentDescription?: string }): Package | null => {
      const box = boxResult?.box;
      if (!box) return null;

      const selectedPackage = findPackageByBox(box);
      const dimensions = formatBoxDimensions(box);
      const weight = calculatePackageWeight(boxResult.itemPositions, selectedPackage as PackageWithTareWeight | null, sellerWeightUnit);

      return {
        packageId: (selectedPackage?.packageId as string) ?? buildCompositePackageId(selectedPackage),
        packageTypeId: (selectedPackage?.packageTypeId as string) ?? STANDARD_PACKAGE_ID,
        carrierId: (selectedPackage?.carrierId as number) ?? null,
        isCustom: (selectedPackage?.isCustom as boolean) ?? true,
        name: (selectedPackage?.name as string) ?? box.name,
        packageTypeName: (selectedPackage?.name as string) ?? box.name,
        dimensions: dimensions as Dimensions,
        weight: weight as Weight,
        insuredValue: existingPackage?.insuredValue ?? { value: 0, code: USD },
        contentDescription: existingPackage?.contentDescription,
      };
    },
    [findPackageByBox, formatBoxDimensions, sellerWeightUnit, buildCompositePackageId],
  );

  const handleApply = useCallback(() => {
    if (!result) {
      return;
    }

    const applyBoxes: BoxResult[] = result?.boxes ?? [];
    const isMultiBox = (result?.isMultiBox ?? false) && applyBoxes.length > 0;
    const packageName = isMultiBox
      ? intl.formatMessage(messages.packagesConfigured, { count: applyBoxes.length })
      : intl.formatMessage(messages.packageConfigured, { name: result?.optimalBox?.name ?? '' });
    const firstExistingPackage = currentPackages[0];

    let packagesToSave: Package[] = [];

    if (isMultiBox) {
      const packages = applyBoxes.map((box) => convertBoxToPackage(box)).filter((pkg): pkg is NonNullable<typeof pkg> => pkg !== null);
      if (!packages.length || !onMultiPackageSave) return;

      packagesToSave = isMultiPackageItemAllocationEnabled ? populatePackagesWithAssignments(packages, result, orderItems) : packages;
      onMultiPackageSave(packagesToSave);

      isApplyingRef.current = true;
    } else {
      const optimalBox = result?.optimalBox;
      if (!optimalBox || !onMultiPackageSave) return;

      const singleBoxResult = { box: optimalBox, itemPositions: result?.itemPositions ?? [] };
      const pkg = convertBoxToPackage(singleBoxResult, firstExistingPackage);
      if (!pkg) return;

      packagesToSave = isMultiPackageItemAllocationEnabled ? populatePackagesWithAssignments([pkg], result, orderItems) : [pkg];
      onMultiPackageSave(packagesToSave);

      isApplyingRef.current = true;
    }

    setAppliedPackageName(packageName ?? null);
    appliedPackageNameRef.current = packageName ?? null;
    hasDismissedRef.current = false;
    setIsApplied(true);

    const singleBoxEntry = result.optimalBox ? [{ box: result.optimalBox, itemPositions: result.itemPositions }] : [];

    markPackingApplied({
      fulfillmentPlanId,
      packageName: packageName ?? '',
      isMultiBox,
      boxes: isMultiBox ? applyBoxes : singleBoxEntry,
      appliedAssignments: isMultiPackageItemAllocationEnabled ? packagesToSave.map((p) => p.assignedItems ?? []) : undefined,
      appliedItemsSignature: buildItemsSignature((fulfillmentPlan?.items ?? emptyArray) as OrderItem[]),
    });

    logPackingRecommendationActivity({
      fulfillmentPlanId,
      action: PackingRecommendationAction.Applied,
      packageName: packageName ?? '',
      boxes: isMultiBox
        ? applyBoxes.map((box) => ({ boxTypeName: box.box?.name ?? '', utilization: box.utilization }))
        : [{ boxTypeName: result.optimalBox?.name ?? '', utilization: result.efficiency }],
    });
    trackPackingRecommendationApplied(fulfillmentPlanId, packageName ?? '', isMultiBox, isMultiBox ? applyBoxes.length : 1);
  }, [
    result,
    fulfillmentPlanId,
    intl,
    convertBoxToPackage,
    onMultiPackageSave,
    orderItems,
    currentPackages,
    isMultiPackageItemAllocationEnabled,
    fulfillmentPlan,
  ]);

  const dismissApplied = useCallback(() => {
    resetPackingApplied(fulfillmentPlanId);
    markPersistedAppliedDismissed(fulfillmentPlanId);
    clearAppliedState();
  }, [fulfillmentPlanId, clearAppliedState]);

  const resetApplied = useCallback(() => {
    dismissApplied();
    if (!onMultiPackageSave) return;

    const [firstPackage] = currentPackages;
    const clearedPackage = {
      packageId: buildCompositePackageId(null),
      packageTypeId: STANDARD_PACKAGE_ID,
      carrierId: null,
      isCustom: false,
      dimensions: { length: 0, width: 0, height: 0, unit: sellerDimensionUnit } as Dimensions,
      weight: { value: 0, unit: sellerWeightUnit } as Weight,
      insuredValue: firstPackage?.insuredValue ?? { value: 0, code: USD },
      contentDescription: firstPackage?.contentDescription,
      assignedItems: [],
    } as unknown as Package;

    isApplyingRef.current = true;
    onMultiPackageSave([clearedPackage]);
  }, [dismissApplied, onMultiPackageSave, currentPackages, buildCompositePackageId, sellerDimensionUnit, sellerWeightUnit]);

  const patchPersistedStatus = useCallback(
    (status: PackingRecommendationStatus) => {
      if (!fulfillmentPlan) return;
      dispatch(
        updateFulfillmentPlanEntitiesOptimistically(
          [fulfillmentPlanId],
          [{ ...fulfillmentPlan, packingRecommendationStatus: status, packingRecommendationFailureReason: null }],
        ),
      );
    },
    [dispatch, fulfillmentPlan, fulfillmentPlanId],
  );

  const logDismissOnce = useCallback(
    (trackDismissal: typeof trackPackingRecommendationDismissed) => {
      if (hasDismissedRef.current) return;
      hasDismissedRef.current = true;
      logPackingRecommendationActivity({
        fulfillmentPlanId,
        action: PackingRecommendationAction.Dismissed,
        packageName: appliedPackageNameRef.current ?? '',
      });
      trackDismissal(fulfillmentPlanId, appliedPackageNameRef.current ?? '');
      patchPersistedStatus(PackingRecommendationStatus.Dismissed);
    },
    [fulfillmentPlanId, patchPersistedStatus],
  );

  const clearApplied = useCallback(() => {
    logDismissOnce(trackPackingRecommendationCleared);
    resetApplied();
  }, [logDismissOnce, resetApplied]);

  const currentItemsSignature = buildItemsSignature((fulfillmentPlan?.items ?? emptyArray) as OrderItem[]);
  useEffect(() => {
    if (!isApplied) return;
    const entry = getAppliedState(fulfillmentPlanId);
    if (!entry?.appliedItemsSignature || entry.appliedItemsSignature === currentItemsSignature) return;
    resetPackingApplied(fulfillmentPlanId);
    patchPersistedStatus(PackingRecommendationStatus.Stale);
  }, [isApplied, currentItemsSignature, fulfillmentPlanId, patchPersistedStatus]);

  // Rebuilds the applied package(s) from the originally-applied boxes and re-saves them.
  const revertToApplied = useCallback(() => {
    const currentState = getAppliedState(fulfillmentPlanId);
    if (!currentState || !onMultiPackageSave) return;

    const packagesToRevert = currentState.boxes
      .map((box) => convertBoxToPackage(box, currentState.isMultiBox ? undefined : currentPackages[0]))
      .filter((pkg): pkg is NonNullable<typeof pkg> => pkg !== null)
      .map((pkg, idx) => {
        const appliedSnapshot = currentState.appliedPackages?.[idx];
        return {
          ...pkg,
          weight:
            appliedSnapshot?.weight != null
              ? { value: appliedSnapshot.weight, unit: appliedSnapshot.weightUnit ?? sellerWeightUnit }
              : pkg.weight,
          assignedItems: currentState.appliedAssignments?.[idx] ?? pkg.assignedItems,
        };
      });

    if (!packagesToRevert.length) return;

    isApplyingRef.current = true;
    onMultiPackageSave(packagesToRevert);
  }, [fulfillmentPlanId, onMultiPackageSave, convertBoxToPackage, currentPackages, sellerWeightUnit]);

  useEffect(() => {
    const packages = currentPackages;
    const havePackagesChanged = lastSeenPackagesRef.current !== packages;
    lastSeenPackagesRef.current = packages;

    const isOrderLevelInstance = !currentPackagesOverride;
    // The drawer instance only re-baselines after its own Apply/Clear: its local packages are converted to the
    // seller's units and padded with an empty package, so diffing them would flag every opened multi-package
    // order. Drawer edits are checked once saved, through the order-level instance.
    const isSystemPackageChange = isOrderLevelInstance && havePackagesChanged && consumeSystemPackageChange(fulfillmentPlanId);
    const hasUserPackageEdit = isOrderLevelInstance && havePackagesChanged && consumeUserPackageEdit(fulfillmentPlanId);

    const isNonUserChangeToApplied =
      isOrderLevelInstance &&
      isApplied &&
      !hasGuardedChangeRef.current &&
      (isSystemPackageChange || (havePackagesChanged && !hasUserPackageEdit));

    if (isApplyingRef.current || isNonUserChangeToApplied) {
      const snapshot = packages.map(toAppliedSnapshot);
      setAppliedValues(snapshot);
      const currentState = getAppliedState(fulfillmentPlanId);
      if (currentState) {
        markPackingApplied({
          fulfillmentPlanId,
          packageName: currentState.packageName,
          isMultiBox: currentState.isMultiBox,
          boxes: currentState.boxes,
          appliedPackages: snapshot,
          appliedAssignments:
            !isApplyingRef.current && currentState.appliedAssignments
              ? packages.map((p: { assignedItems?: AssignedItem[] }) => p.assignedItems ?? [])
              : currentState.appliedAssignments,
        });
      }
      isApplyingRef.current = false;
      return;
    }

    if (!hasCheckedInitialDivergenceRef.current) {
      hasCheckedInitialDivergenceRef.current = true;
      return;
    }

    if (!isApplied || !appliedValues || !hasUserPackageEdit) {
      return;
    }

    if (isMultiPackageDrawerOpen(fulfillmentPlanId)) {
      return;
    }

    const packageCountChanged = packages.length !== appliedValues.length;
    const storedAppliedAssignments = getAppliedState(fulfillmentPlanId)?.appliedAssignments;

    const hasFieldChange = packages.some((p, idx: number) => {
      const saved = appliedValues[idx];
      if (!saved || hasRecommendedFieldChanged(p, saved)) return true;
      const appliedForPackage = storedAppliedAssignments?.[idx];
      return appliedForPackage !== undefined && assignmentsChanged(appliedForPackage, p.assignedItems ?? []);
    });

    const hasAnyChange = packageCountChanged || hasFieldChange;

    if (hasAnyChange) {
      if (hasGuardedChangeRef.current) return;
      hasGuardedChangeRef.current = true;
      packingRecommendationGuard.guardOverride(
        () => {
          hasGuardedChangeRef.current = false;
          logDismissOnce(trackPackingRecommendationDismissed);
          dismissApplied();
        },
        appliedPackageNameRef.current ?? '',
        () => {
          hasGuardedChangeRef.current = false;
          revertToApplied();
        },
      );
    }
  }, [
    currentPackages,
    currentPackagesOverride,
    isApplied,
    appliedValues,
    fulfillmentPlanId,
    dismissApplied,
    logDismissOnce,
    packingRecommendationGuard,
    revertToApplied,
  ]);

  return { handleApply, isApplied, appliedPackageName, clearApplied };
};
